'use client'

import Image from 'next/image'
import Link from 'next/link'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import FadeIn from '@/components/ui/FadeIn'

interface FeatureCard {
	id: string
	icon?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	text?: string
}

interface WhatSetsApartData {
	anchor_title?: string
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	background?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	features?: FeatureCard[]
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title: string
					text: string
				}
			}[]
		}
	}[]
	button?: {
		href: string
		label: string
	}
	bottom_heading?: {
		title?: string
	}
	stats?: {
		id: string
		text?: string
	}[]
}

export const fields = [
	'anchor_title',
	'heading.title',
	'text.text',
	'background.alt',
	'background.image_file.image.id',
	'background.image_file.image.filename_download',
	'features.id',
	'features.icon.alt',
	'features.icon.image_file.image.id',
	'features.icon.image_file.image.filename_download',
	'features.text',
	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
	'button.href',
	'button.label',
	'bottom_heading.title',
	'stats.id',
	'stats.text',
] as const

interface WhatSetsApartProps {
	data?: WhatSetsApartData
}

export default function WhatSetsApart({ data }: WhatSetsApartProps) {
	const { ref, isVisible } = useScrollAnimation({
		threshold: 0.05,
		rootMargin: '0px',
	})
	const sectionId = (data?.anchor_title || 'WhatSetsApart')
		.replace(/\s+/g, '-')
		.replace(/[^a-zA-Z0-9-_]/g, '')
		.replace(/-{2,}/g, '-')

	const featureCards = data?.features || []
	const bottomItems = data?.content_collection || []

	return (
		<>
		<section
			id={sectionId}
			data-name={data?.anchor_title || 'WhatSetsApart'}
			ref={ref}
			className='WhatSetsApart relative w-full pb-12 md:pb-20 overflow-hidden md:bg-transparent bg-[#60676A]'
		>
			<div className='absolute inset-0 hidden md:block'>
				{data?.background?.image_file?.image?.id && (
					<Image
						src={getReadableImageUrl(
							data.background.image_file.image.id,
							data.background.image_file.image.filename_disk || ''
						)}
						alt={data.background.alt || ''}
						fill
						className='object-cover object-center'
					/>
				)}
			</div>

			<div className='container relative z-10'>
				<div className='relative'>
					<div className='absolute right-full top-0 w-screen h-full bg-[rgba(178,164,165,0.66)]' />

					<FadeIn
						isVisible={isVisible}
						delay={0}
						direction='right'
						className='relative w-full md:w-1/2 bg-[rgba(178,164,165,0.66)] py-12 md:pr-12 xl:pr-20 xl:py-20 md:rounded-r-lg'
					>
						<h2>{data?.heading?.title || ''}</h2>

						<div
							className='grid gap-4 font-sans text-base font-normal text-black'
							dangerouslySetInnerHTML={sanitizeHTML(
								data?.text?.text || ''
							)}
						/>
					</FadeIn>

					<div className='absolute left-full top-0 w-screen h-full bg-[rgba(178,164,165,0.66)] md:hidden' />
				</div>
			</div>

			<FadeIn isVisible={isVisible} delay={100} className='md:hidden w-full'>
				{data?.background?.image_file?.image?.id && (
					<Image
						src={getReadableImageUrl(
							data.background.image_file.image.id,
							data.background.image_file.image.filename_disk || ''
						)}
						alt={data.background.alt || ''}
						width={768}
						height={400}
						className='w-full h-auto'
					/>
				)}
			</FadeIn>

			<div className='container relative z-10'>
				{featureCards.length > 0 && (
					<div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-12 mb-8'>
						{featureCards.map((card, index) => (
							<FadeIn
								key={`feature-${card.id}-${index}`}
								isVisible={isVisible}
								delay={200 + index * 80}
								direction='up'
								className='p-5 bg-[rgba(98,89,89,0.90)] backdrop-blur-[13.30px] text-center flex flex-col items-center'
							>
								{card.icon?.image_file?.image?.id && (
									<div className='mb-3'>
										<Image
											src={getReadableImageUrl(
												card.icon.image_file.image.id,
												card.icon.image_file.image.filename_disk || ''
											)}
											alt={card.icon.alt || ''}
											width={50}
											height={50}
											style={{ width: '3.125rem', height: '3.125rem' }}
											className='mx-auto'
										/>
									</div>
								)}
								{card.text && (
									<p className='text-white text-base font-normal font-sans text-center tracking-wide'>
										{card.text}
									</p>
								)}
							</FadeIn>
						))}
					</div>
				)}

				{bottomItems.length > 0 && (
					<div className='grid md:grid-cols-3 gap-8 mb-8'>
						{bottomItems.map((item, index) => {
							const titleContent = item?.site_content_collections_id?.collection?.find(c => c.item.title)?.item.title
							const textContent = item?.site_content_collections_id?.collection?.find(c => c.item.text)?.item.text

							return (
								<FadeIn
									key={`bottom-${item.id}-${index}`}
									isVisible={isVisible}
									delay={200 + featureCards.length * 80 + index * 100}
									direction='up'
									className='p-9 bg-[rgba(98,89,89,0.90)] backdrop-blur-[13.30px]'
								>
									{titleContent && (
										<div
											className='text-white text-2xl md:text-3xl font-normal font-sans mb-4 text-center'
											dangerouslySetInnerHTML={sanitizeHTML(titleContent)}
										/>
									)}
									{textContent && (
										<div
											className='text-white text-base font-normal font-sans text-center tracking-wide'
											dangerouslySetInnerHTML={sanitizeHTML(textContent)}
										/>
									)}
								</FadeIn>
							)
						})}
					</div>
				)}

				{data?.button && (
					<FadeIn
						isVisible={isVisible}
						delay={200 + featureCards.length * 80 + bottomItems.length * 100 + 100}
						className='flex justify-center'
					>
						<Link
							href={data.button?.href || '/'}
							className='btn'
							onClick={e => {
								if (data.button?.href) {
									e.preventDefault()
									window.location.href = data.button.href
								}
							}}
						>
							{data.button.label}
						</Link>
					</FadeIn>
				)}
			</div>

		</section>

			{(data?.bottom_heading?.title || (data?.stats && data.stats.length > 0)) && (
				<div className='bg-[#CAC1C1]'>
					<div className='container py-10'>
						{data?.bottom_heading?.title && (
							<div className='text-center mb-8'>
								<h4 className='font-sans text-xl md:text-3xl leading-8 md:leading-10 font-semibold tracking-[0.07rem] uppercase text-[#030303]'>
									{data.bottom_heading.title}
								</h4>
							</div>
						)}
						{data?.stats && data.stats.length > 0 && (
							<div className='flex flex-col md:flex-row items-center justify-center md:divide-x md:divide-black/30'>
								{data.stats.map((stat, index) => (
									<div
										key={`stat-${stat.id}-${index}`}
										className='px-8 py-3 md:py-0 text-center md:text-left font-freightBig text-2xl leading-8 font-normal uppercase tracking-[0.15rem] text-black'
										dangerouslySetInnerHTML={{ __html: stat.text }}
									/>
								))}
							</div>
						)}
					</div>
				</div>
			)}
		</>
	)
}
